import React, { useState, useEffect, useRef } from 'react';
import { Table, Input, Button, Dropdown, Radio, Tooltip } from 'antd';
import { EditOutlined, DownOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './TableUser.scss';
import dayjs from 'dayjs';

const TableUser = ({
  data,
  tableParams,
  onChangePage,
  loading,
  totalElements,
  onDeleteItem,
  updatePassword,
  updateUser,
  groupList = [],
  updateUserGroup,
}) => {
  const { t } = useTranslation();

  const [editingPasswordRow, setEditingPasswordRow] = useState(null);
  const [editingCompanyRow, setEditingCompanyRow] = useState(null);
  const [passwords, setPasswords] = useState({});
  const [companies, setCompanies] = useState({});
  const [userColors, setUserColors] = useState({});
  const [openGroupDropdown, setOpenGroupDropdown] = useState(null);

  // 드롭다운 컨테이너 ref 추가
  const dropdownContainerRef = useRef({});

  // useEffect를 사용하여 새로운 사용자의 색상을 추가
  useEffect(() => {
    const newColors = { ...userColors };
    data.forEach(user => {
      if (!newColors[user.username]) {
        const hue = Math.floor(Math.random() * 360);
        newColors[user.username] = `hsl(${hue}, 70%, 65%)`;
      }
    });
    setUserColors(newColors);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Password 관련 핸들러
  const handlePasswordEditClick = (record) => {
    // console.log('Password Edit Click:', record.id);
    setEditingPasswordRow(record.id);
    setPasswords({
      ...passwords,
      [record.id]: ''
    });
  };

  const handlePasswordChange = (e, id) => {
    setPasswords({
      ...passwords,
      [id]: e.target.value,
    });
  };

  const handlePasswordKeyPress = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      updatePassword(id, passwords[id]);
      setEditingPasswordRow(null);
    }
  };

  const handlePasswordBlur = () => {
    setEditingPasswordRow(null);
    setPasswords({});
  };

  // Company 관련 핸들러
  const handleCompanyEditClick = (record) => {
    // console.log('Company Edit Click:', record.id);
    setEditingCompanyRow(record.id);
    setCompanies({
      ...companies,
      [record.id]: record.company
    });
  };

  const handleCompanyChange = (e, id) => {
    setCompanies({
      ...companies,
      [id]: e.target.value,
    });
  };

  const handleCompanyKeyPress = (e, record) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      updateUser({
        id: record.id,
        company: companies[record.id]
      });
      setEditingCompanyRow(null);
    }
  };

  const handleCompanyBlur = () => {
    setEditingCompanyRow(null);
    setCompanies({});
  };

  // Group 관련 핸들러
  const handleGroupDropdownClick = (recordId) => {
    setOpenGroupDropdown(openGroupDropdown === recordId ? null : recordId);
  };
  
  const handleGroupChange = (value, record) => {
    updateUserGroup(value, record);
    setOpenGroupDropdown(null);
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: '12%',
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (username) => {
        const firstLetter = username.charAt(0).toUpperCase();
        // 기존 색상이 있으면 사용하고, 없으면 새로 생성
        const backgroundColor = userColors[username];
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                backgroundColor,
                color: '#fff',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              {firstLetter}
            </div>
            <span>{username}</span>
          </div>
        );
      },
      onCell: () => ({
        style: { cursor: 'default' }
      })
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '15%',
      sorter: (a, b) => a.email.localeCompare(b.email),
      onCell: () => ({
        style: { cursor: 'default' }
      })
    },
    {
      title: 'Password',
      render: (text, record) => (
        <div className="password-cell">
          {editingPasswordRow === record.id ? (
            <Input
              value={passwords[record.id] || ''}
              onChange={(e) => handlePasswordChange(e, record.id)}
              onKeyPress={(e) => handlePasswordKeyPress(e, record.id)}
              onBlur={handlePasswordBlur}
              autoFocus
              maxLength={28}
              placeholder={t('users.placeholder.maxLen')}
            />
          ) : (
            <>
              <span>••••••••••••</span>
              <Tooltip title={t('users.tip.editPassword')} overlayClassName="user-tooltip">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => handlePasswordEditClick(record)}
                  type="link"
                  className="password-edit-button cursor-pointer-column"
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
      width: '11%',
      align: 'center',
      onCell: () => ({
        style: { cursor: 'pointer' }
      })
    },
    {
      title: 'Group',
      dataIndex: 'groupName',
      key: 'groupName',
      width: '16%',
      align: 'center',
      sorter: (a, b) => {
        const groupA = a.groupName || '';
        const groupB = b.groupName || '';
        return groupA.localeCompare(groupB);
      },
      sortField: 'group_name',
      render: (groupName, record) => {
        // 그룹 메뉴 아이템 생성
        const menuItems = [
          // 각 그룹을 개별 메뉴 아이템으로 추가
          ...(groupList && groupList.length > 0 
            ? groupList.map(group => ({
                key: `group-${group.id}`,
                label: (
                  <div 
                    className="group-radio-item" 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (record.groupId !== group.id) {
                        handleGroupChange(group.id, record);
                      }
                    }}
                  >
                    <Radio checked={record.groupId === group.id}>
                      {group.name || group.groupName || t('users.group.fallbackName', { id: group.id })}
                    </Radio>
                  </div>
                )
              }))
            : [{
                key: 'no-groups',
                label: <div style={{ padding: '8px', color: '#999' }}>{t('users.group.empty')}</div>,
                disabled: true
              }]
          ),
          {
            type: 'divider'
          },
          {
            key: 'no-group',
            label: (
              <div 
                className="group-radio-item" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (record.groupId !== 0) {
                    handleGroupChange(0, record);
                  }
                }}
              >
                <Radio checked={record.groupId === 0 || record.groupId === null || record.groupId === undefined}>
                  No Group
                </Radio>
              </div>
            )
          }
        ];
        
        return (
          <div 
            className="dropdown-container" 
            ref={el => dropdownContainerRef.current[record.id] = el}
          >
            <Dropdown 
              menu={{ items: menuItems }}
              trigger={['click']}
              open={openGroupDropdown === record.id}
              onOpenChange={(open) => {
                if (open) {
                  handleGroupDropdownClick(record.id);
                } else {
                  setOpenGroupDropdown(null);
                }
              }}
              getPopupContainer={() => dropdownContainerRef.current[record.id]}
              overlayClassName="group-dropdown-overlay"
              placement="bottomLeft"
            >
              <div className="group-dropdown-trigger">
                <span>{record.groupName || 'No Group'}</span>
                <DownOutlined className="dropdown-icon" />
              </div>
            </Dropdown>
          </div>
        );
      },
      onCell: () => ({
        style: { cursor: 'pointer' }
      })
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      width: '12%',
      align: 'center',
      sorter: (a, b) => a.company.localeCompare(b.company),
      render: (text, record) => (
        <div className="company-cell">
          {editingCompanyRow === record.id ? (
            <Input
              value={companies[record.id] !== undefined ? companies[record.id] : text}
              onChange={(e) => handleCompanyChange(e, record.id)}
              onKeyPress={(e) => handleCompanyKeyPress(e, record)}
              onBlur={handleCompanyBlur}
              autoFocus
              maxLength={28}
              placeholder={t('users.placeholder.maxLen')}
            />
          ) : (
            <>
              <span>{text}</span>
              <Tooltip title={t('users.tip.editCompany')} overlayClassName="user-tooltip">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => handleCompanyEditClick(record)}
                  type="link"
                  className="company-edit-button cursor-pointer-column"
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
      onCell: () => ({
        style: { cursor: 'pointer' }
      })
    },
    {
      title: 'Report Count',
      dataIndex: 'reportCount',
      key: 'reportCount',
      width: '12%',
      align: 'center',
      sorter: {
        compare: (a, b) => a.reportCount - b.reportCount,
        multiple: 1,
      },
      sortField: 'report_count',
      onCell: () => ({
        style: { cursor: 'default' }
      })
    },
    {
      title: 'Register Date',
      dataIndex: 'insDate',
      key: 'insDate',
      width: '15%',
      align: 'center',
      render: (timestamp) => dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'),
      sorter: {
        compare: (a, b) => a.insDate - b.insDate,
        multiple: 1,
      },
      sortField: 'ins_date',
      onCell: () => ({
        style: { cursor: 'default' }
      })
    },
    {
      title: 'Delete',
      key: 'delete',
      width: '7%',
      align: 'center',
      render: (_, record) => {
        // admin 계정인 경우 빈 컨텐츠 반환
        if (record.username === 'admin') {
          return null;
        }
        
        // admin이 아닌 경우에만 삭제 버튼 표시
        return (
          <div className="delete-button-container">
            <Tooltip title={t('users.tip.deleteUser')} overlayClassName="user-tooltip">
              {/* 고정 색 PNG 는 화이트 모드 배경에 묻혀 버려서, 테마 토큰으로
                  색을 줄 수 있는 아이콘 폰트로 대체했습니다. */}
              <DeleteOutlined
                className="delete-button cursor-pointer-column"
                onClick={() => onDeleteItem([record])}
                role="button"
                aria-label={t('users.tip.deleteUser')}
              />
            </Tooltip>
          </div>
        );
      },
      onCell: () => ({
        style: { cursor: 'pointer' }
      })
    },
  ];

  const rowClassName = (record) => {
    return editingPasswordRow === record.id || editingCompanyRow === record.id ? 'selected-row' : 'delete-row';
  };

  return (
    <div className="wrapper-table">
      <Table
        showSorterTooltip={false}
        columns={columns}
        dataSource={(data || []).map(item => ({ ...item, key: item.id }))}
        loading={loading}
        pagination={{
          current: tableParams.pageNumber,
          total: totalElements,
          pageSize: tableParams.pageSize,
          showSizeChanger: false
        }}
        onChange={(pagination, filters, sorter) => {
          // sorter.field에 따라 적절한 필드명으로 변환
          const modifiedSorter = {
            ...sorter,
            field: sorter.field === 'reportCount' ? 'report_count' : 
                   sorter.field === 'insDate' ? 'ins_date' : 
                   sorter.field
          };
          onChangePage(pagination, filters, modifiedSorter);
        }}
        rowClassName={rowClassName}
      />
    </div>
  );
};

export default TableUser;